import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './ProductList.css';

const PRODUCTS_PER_PAGE = 24;

const toCloudinaryThumb = (url) => {
  if (!url || !url.includes('cloudinary.com')) return url;
  return url.replace('/upload/', '/upload/w_400/h_400/c_pad/b_rgb:f5f5f5/q_auto:good/f_auto/');
};

const formatPrice = (value) => Number(value).toLocaleString('tr-TR', { minimumFractionDigits: 0 });

// İndirimli fiyat gösterimi — eski fiyat üstü çizili + yeni fiyat + yüzde fark, rozet yok
function PriceDisplay({ product }) {
  const price = Number(product.price);
  const compareAt = product.compare_at_price ? Number(product.compare_at_price) : null;
  if (!compareAt || compareAt <= price) {
    return <>₺{formatPrice(price)}</>;
  }
  const percentOff = Math.round((1 - price / compareAt) * 100);
  return (
    <>
      <span style={{ textDecoration: 'line-through', color: '#999', fontSize: '0.85em', marginRight: '6px' }}>
        ₺{formatPrice(compareAt)}
      </span>
      <span style={{ color: '#e74c3c', fontWeight: 'bold' }}>₺{formatPrice(price)}</span>
      <span style={{ color: '#e74c3c', fontSize: '0.85em', marginLeft: '4px' }}>(-%{percentOff})</span>
    </>
  );
}

function ProductList() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [favorites, setFavorites] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('kategori') || 'all');
  const [sortBy, setSortBy] = useState('newest');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [hoveredImageIndex, setHoveredImageIndex] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [subcategories, setSubcategories] = useState([]);
  const [selectedSubcategories, setSelectedSubcategories] = useState([]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    const kategori = searchParams.get('kategori');
    setSelectedCategory(kategori || 'all');
    setCurrentPage(1);
    setSelectedSubcategories([]);
  }, [searchParams]);

  useEffect(() => {
    if (selectedCategory === 'all') { setSubcategories([]); setSelectedSubcategories([]); return; }
    fetch(`${process.env.REACT_APP_API_URL}/api/subcategories?category=${encodeURIComponent(selectedCategory)}`)
      .then(r => r.ok ? r.json() : [])
      .then(data => setSubcategories(data))
      .catch(() => setSubcategories([]));
    setSelectedSubcategories([]);
  }, [selectedCategory]);

  const loadFavorites = () => {
    setFavorites(JSON.parse(localStorage.getItem('favorites') || '[]'));
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [sortBy, debouncedSearchQuery, selectedSubcategories]);

  useEffect(() => {
    loadFavorites();
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams();
        if (selectedCategory !== 'all') params.append('category', selectedCategory);
        if (sortBy !== 'newest') params.append('sortBy', sortBy);
        if (debouncedSearchQuery.trim()) params.append('search', debouncedSearchQuery.trim());
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/products?${params.toString()}`);
        if (response.ok) {
          const data = await response.json();
          // Stoklu ürünler önce, tükenmiş ürünler sona
          data.sort((a, b) => {
            const aOos = (a.stock || 0) === 0;
            const bOos = (b.stock || 0) === 0;
            if (aOos === bOos) return 0;
            return aOos ? 1 : -1;
          });
          setProducts(data);
        } else {
          setProducts([]);
        }
      } catch {
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [selectedCategory, sortBy, debouncedSearchQuery]);

  const isFavorite = (id) => favorites.some(f => f.id === id);

  const getProductImages = (product) => {
    if (!product.images) return [];
    if (typeof product.images === 'string') {
      try {
        const parsed = JSON.parse(product.images);
        if (Array.isArray(parsed)) return parsed;
        if (parsed && typeof parsed === 'object') return Object.values(parsed).filter(u => u);
        return [];
      } catch {
        return product.images.split(',').map(u => u.trim()).filter(u => u);
      }
    }
    return Array.isArray(product.images) ? product.images : [];
  };

  const toggleFavorite = (product, e) => {
    e.stopPropagation();
    const current = JSON.parse(localStorage.getItem('favorites') || '[]');
    const imgs = getProductImages(product);
    const next = isFavorite(product.id)
      ? current.filter(f => f.id !== product.id)
      : [...current, { id: product.id, name: product.name, price: product.price, image: imgs[0] || null }];
    localStorage.setItem('favorites', JSON.stringify(next));
    setFavorites(next);
  };

  const filteredProducts = selectedSubcategories.length > 0
    ? products.filter(p => selectedSubcategories.includes(p.subcategory))
    : products;

  const totalPages = Math.ceil(filteredProducts.length / PRODUCTS_PER_PAGE);
  const startIdx = (currentPage - 1) * PRODUCTS_PER_PAGE;
  const endIdx = Math.min(startIdx + PRODUCTS_PER_PAGE, filteredProducts.length);
  const paginatedProducts = filteredProducts.slice(startIdx, endIdx);

  const toggleSubcategory = (name) => {
    setSelectedSubcategories(prev =>
      prev.includes(name) ? prev.filter(s => s !== name) : [...prev, name]
    );
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Ana Sayfa', item: 'https://ipekaksesuar.com' },
      { '@type': 'ListItem', position: 2, name: 'Ürünler', item: 'https://ipekaksesuar.com/urunler' }
    ]
  };

  return (
    <>
      <Helmet>
        <title>Tüm Ürünler | İpek Aksesuar</title>
        <meta name="description" content="İpek Aksesuar'ın tüm ürünlerini keşfedin. El işçiliği ile üretilmiş premium kalite ipek eşarplar, şallar ve aksesuarlar." />
        <link rel="canonical" href="https://ipekaksesuar.com/urunler" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Tüm Ürünler | İpek Aksesuar" />
        <meta property="og:description" content="İpek Aksesuar'ın tüm ürünlerini keşfedin." />
        <meta property="og:url" content="https://ipekaksesuar.com/urunler" />
        <meta property="og:locale" content="tr_TR" />
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <div className="pl-container">
        {/* Başlık */}
        <div className="pl-header">
          <h1 className="pl-title">Tüm Ürünler</h1>

          {/* Filtre satırı */}
          <div className="pl-filters">
            <input
              className="pl-search"
              type="text"
              placeholder="Ürün ara..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />

            <select
              className="pl-select"
              value={selectedCategory}
              onChange={(e) => {
                const val = e.target.value;
                setSelectedCategory(val);
                const params = new URLSearchParams(searchParams);
                if (val === 'all') params.delete('kategori');
                else params.set('kategori', val);
                window.history.replaceState(null, '', `?${params.toString()}`);
              }}
            >
              <option value="all">Tüm Kategoriler</option>
              <option value="Eşarp">Eşarp</option>
              <option value="Şal">Şal</option>
              <option value="Fular">Fular</option>
              <option value="Twilly">Twilly</option>
            </select>

            <select
              className="pl-select"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="newest">En Yeni</option>
              <option value="name">İsme Göre (A-Z)</option>
              <option value="price-asc">Fiyat ↑</option>
              <option value="price-desc">Fiyat ↓</option>
            </select>

            {(selectedCategory !== 'all' || sortBy !== 'newest' || debouncedSearchQuery.trim() || selectedSubcategories.length > 0) && (
              <button
                className="pl-clear"
                onClick={() => { setSelectedCategory('all'); setSortBy('newest'); setSearchQuery(''); setSelectedSubcategories([]); window.history.replaceState(null, '', window.location.pathname); }}
              >
                Temizle
              </button>
            )}
          </div>

          {/* Subcategory Bubble'ları */}
          {subcategories.length > 0 && (
            <div className="pl-bubbles">
              {subcategories.map(s => {
                const active = selectedSubcategories.includes(s.name);
                return (
                  <button
                    key={s.id}
                    className={`pl-bubble${active ? ' active' : ''}`}
                    onClick={() => toggleSubcategory(s.name)}
                  >
                    {s.name}
                    {active && <span className="pl-bubble-x">×</span>}
                  </button>
                );
              })}
            </div>
          )}

          <p className="pl-count">
            {filteredProducts.length > 0
              ? `${startIdx + 1}–${endIdx} arası gösteriliyor (toplam ${filteredProducts.length} ürün)`
              : '0 ürün'
            }
          </p>
        </div>

        {/* Loading */}
        {loading ? (
          <div className="pl-loading">
            <div className="pl-spinner" />
          </div>
        ) : products.length === 0 ? (
          <div className="pl-empty">
            <span className="pl-empty-icon">📦</span>
            <p>Ürün bulunamadı.</p>
          </div>
        ) : (
          <div className="pl-grid">
            {paginatedProducts.map(product => {
              const imgs = getProductImages(product);
              const imgIdx = hoveredImageIndex[product.id] ?? 0;
              const fav = isFavorite(product.id);

              const isOos = (product.stock || 0) === 0;
              return (
                <div
                  key={product.id}
                  className={`pl-card${isOos ? ' pl-card--oos' : ''}`}
                  onClick={() => navigate(`/urun/${product.slug || product.id}`)}
                  onMouseMove={(e) => {
                    if (imgs.length <= 1) return;
                    const rect = e.currentTarget.getBoundingClientRect();
                    const x = e.clientX - rect.left;
                    const idx = Math.min(
                      Math.floor((x / rect.width) * imgs.length),
                      imgs.length - 1
                    );
                    setHoveredImageIndex(prev => ({ ...prev, [product.id]: idx }));
                  }}
                  onMouseLeave={() =>
                    setHoveredImageIndex(prev => ({ ...prev, [product.id]: 0 }))
                  }
                  onTouchStart={(e) => {
                    if (imgs.length <= 1) return;
                    e.currentTarget._touchStartX = e.touches[0].clientX;
                  }}
                  onTouchEnd={(e) => {
                    if (imgs.length <= 1) return;
                    const startX = e.currentTarget._touchStartX;
                    if (startX == null) return;
                    const deltaX = startX - e.changedTouches[0].clientX;
                    if (Math.abs(deltaX) < 20) return;
                    const dir = deltaX > 0 ? 1 : -1;
                    setHoveredImageIndex(prev => {
                      const cur = prev[product.id] ?? 0;
                      const next = Math.max(0, Math.min(cur + dir, imgs.length - 1));
                      return { ...prev, [product.id]: next };
                    });
                  }}
                >
                  {/* Fotoğraf */}
                  <div className="pl-img-wrap">
                    {imgs.length > 0 ? (
                      <img
                        src={toCloudinaryThumb(imgs[imgIdx])}
                        alt={product.name}
                        className="pl-img"
                        loading="lazy"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <div className="pl-img-placeholder">🧣</div>
                    )}

                    {/* Çubuk göstergesi */}
                    {imgs.length > 1 && (
                      <div className="pl-progress">
                        {imgs.map((_, i) => (
                          <div
                            key={i}
                            className={`pl-progress-seg${i === imgIdx ? ' active' : ''}`}
                          />
                        ))}
                      </div>
                    )}

                    {/* Favori butonu */}
                    <button
                      className={`pl-fav${fav ? ' active' : ''}`}
                      onClick={(e) => toggleFavorite(product, e)}
                      title={fav ? 'Favorilerden Kaldır' : 'Favorilere Ekle'}
                    >
                      {fav ? (
                        <svg viewBox="0 0 24 24" width="18" height="18" fill="#e74c3c">
                          <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                        </svg>
                      ) : (
                        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="#333" strokeWidth="1.8">
                          <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                        </svg>
                      )}
                    </button>
                  </div>

                  {/* Alt bilgi */}
                  <div className="pl-info">
                    <span className="pl-name">{product.name}</span>
                    {isOos
                      ? <span className="pl-oos-label">Tükendi</span>
                      : <span className="pl-price"><PriceDisplay product={product} /></span>
                    }
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <div className="pl-pagination">
            <button
              className="pl-page-btn pl-page-nav"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              ‹
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                className={`pl-page-btn${page === currentPage ? ' active' : ''}`}
                onClick={() => handlePageChange(page)}
              >
                {page}
              </button>
            ))}

            <button
              className="pl-page-btn pl-page-nav"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              ›
            </button>
          </div>
        )}
      </div>
    </>
  );
}

export default ProductList;
